/* eslint-disable react/prop-types */
/*
  PreviewCanvas — multi-page visual preview.

  Renders the resume as N **separate** A4 "paper" cards stacked vertically with
  a clear visual gap between pages (just like Google Docs / Word print view).
  Each page card shows only its own slice of the continuous ResumeRenderer
  output by clipping + translating, so the text wrapping / typography is
  byte-identical to the Playwright PDF.

  How pages are counted:
    - Content height is measured after layout settles.
    - Manual `[data-page-break]` markers (emitted by ResumeRenderer for
      `page_break_after` + `section_breaks`) force a page split at that
      Y-position.
    - Any remaining content longer than one page height auto-splits at page
      boundaries, so overflow naturally wraps to the next page visually.

  Reports { heightMm, pages } back via onMetrics.
*/
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ResumeRenderer from "./ResumeRenderer";

const MM_PER_PX = 1 / 3.7795275591; // 96 DPI assumption (matches Chromium PDF)
const PX_PER_MM = 3.7795275591;
const PAGE_GAP_PX = 28; // Visible gap between page cards in the preview

const PAGE_DIMS_MM = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Legal: { w: 215.9, h: 355.6 },
};

export default function PreviewCanvas({ data, style, onMetrics, maxScale = 1, editable = false, onTextEdit }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [scale, setScale] = useState(0.6);
  const [contentH, setContentH] = useState(0); // measured mm (unclipped renderer height)
  // pageSplitsMm: array of y-offsets (in mm) where each page STARTS. First entry is always 0.
  const [pageSplitsMm, setPageSplitsMm] = useState([0]);

  const dims = style?.page_size === "Custom"
    ? { w: Number(style.custom_width_mm) || 210, h: Number(style.custom_height_mm) || 297 }
    : PAGE_DIMS_MM[style?.page_size] || PAGE_DIMS_MM.A4;

  // Per-resume margin override takes precedence over the template's margin
  const marginMm = Math.max(0, Number(
    data?.page_margin_mm_override != null ? data.page_margin_mm_override : (style?.page_margin_mm ?? 12)
  ));
  const heroEdge = !!style?.hero_enabled;
  const pageContentH = dims.h - 2 * marginMm; // content area height per page (margins stripped)
  const firstPageContentH = heroEdge ? dims.h - marginMm : pageContentH; // hero bleeds top
  const pages = pageSplitsMm.length;

  // Recompute scale on container resize
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const update = () => {
      const w = containerRef.current?.clientWidth || 0;
      if (w > 0) {
        const pageWpx = dims.w * PX_PER_MM;
        const next = Math.min(maxScale, w / pageWpx);
        setScale(Number.isFinite(next) && next > 0 ? next : 1);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [dims.w, maxScale]);

  // Measure rendered content + detect manual page-break markers, then compute splits.
  useEffect(() => {
    let raf1, raf2;
    const measure = () => {
      const el = measureRef.current;
      if (!el) return;
      const containerRect = el.getBoundingClientRect();
      // Measure in mm by converting scrollHeight from px.
      const totalPx = el.scrollHeight;
      const totalMm = totalPx * MM_PER_PX;
      setContentH(totalMm);

      // Find manual break markers in DOM order (they are zero-height divs)
      const breakEls = el.querySelectorAll("[data-page-break]");
      const manualBreaksMm = [];
      breakEls.forEach((bel) => {
        const rect = bel.getBoundingClientRect();
        const yPx = rect.top - containerRect.top;
        const yMm = yPx * MM_PER_PX;
        if (yMm > 1) manualBreaksMm.push(yMm);
      });

      // Find every non-breakable block — these MUST stay on one page.
      // ResumeRenderer already sets `pageBreakInside: avoid` inline on every
      // non-breakable group (role card, education item, project item, bullet,
      // highlight tile, hero, etc.). We pick the OUTERMOST blocks so the
      // whole "role header + bullets" group stays together when it fits.
      // If an outermost block is taller than one page, we let it through
      // and recurse into its inner avoid-break children so individual
      // bullets are still protected.
      const allAvoid = Array.from(el.querySelectorAll('[style*="page-break-inside"]'));
      const isOuter = (node) => {
        let p = node.parentElement;
        while (p && p !== el) {
          if (p.style && p.style.pageBreakInside === "avoid") return false;
          p = p.parentElement;
        }
        return true;
      };
      const avoid = [];
      const collect = (node) => {
        const rect = node.getBoundingClientRect();
        const topMm = (rect.top - containerRect.top) * MM_PER_PX;
        const bottomMm = (rect.bottom - containerRect.top) * MM_PER_PX;
        const heightMm = bottomMm - topMm;
        if (heightMm <= 0.2) return;
        // If this block fits on one page, protect the whole thing.
        // If not, recurse into its inner avoid children so bullets still get
        // protected individually.
        if (heightMm <= pageContentH - 2) {
          avoid.push({ top: topMm, bottom: bottomMm });
        } else {
          const inner = Array.from(node.querySelectorAll('[style*="page-break-inside"]'));
          for (const c of inner) {
            // Only first-level inner avoid descendants (skip nested)
            let p = c.parentElement, firstLevel = true;
            while (p && p !== node) {
              if (p.style && p.style.pageBreakInside === "avoid") { firstLevel = false; break; }
              p = p.parentElement;
            }
            if (firstLevel) collect(c);
          }
        }
      };
      for (const ael of allAvoid) {
        if (isOuter(ael)) collect(ael);
      }
      avoid.sort((a, b) => a.top - b.top);

      const pageH = (idx) => (idx === 0 ? firstPageContentH : pageContentH);

      // Find the smart split position when an auto-split would otherwise
      // land inside a non-breakable block. We pull the split UP to just
      // before the offending block. If that would make the resulting page
      // < 30% of pageH, we keep the original cut (block will be clipped —
      // but this only happens for unrealistically tall blocks).
      const snapSplit = (cursor, naturalCut, pageHeight) => {
        // Walk intervals; if the natural cut Y is inside one, snap to top.
        for (const iv of avoid) {
          if (iv.top >= naturalCut) break;       // future intervals
          if (iv.bottom <= cursor) continue;     // already-past intervals
          if (naturalCut > iv.top && naturalCut < iv.bottom) {
            const proposed = iv.top - 0.2; // 0.2mm gutter
            if (proposed - cursor > pageHeight * 0.15) {
              return proposed; // accept smart pullback (≥ 15% page = acceptable)
            }
            // Otherwise the block alone is taller than 70% of a page — cut anyway
            return naturalCut;
          }
        }
        return naturalCut;
      };

      // Build page splits:
      //   splits[0] = 0 (page 1 starts at content-top)
      //   After each forced manual break → start a new page at that y
      //   Between splits, if gap > pageContentH → smart-auto-split before
      //     any non-breakable group that the natural cut would split.
      const splits = [0];
      let cursor = 0;
      const forced = [...manualBreaksMm].sort((a, b) => a - b);

      const consumeUntil = (targetY) => {
        // Slice the gap [cursor, targetY] into page-sized chunks, snapping
        // each natural cut to just-before any non-breakable group.
        // Safety cap to avoid runaway loops on bad data.
        for (let safety = 0; safety < 50; safety++) {
          const ph = pageH(splits.length - 1);
          const naturalCut = cursor + ph;
          if (targetY - cursor <= ph + 0.5) break;
          const smart = snapSplit(cursor, naturalCut, ph);
          // If smart pullback didn't move forward (would loop), use naturalCut
          const next = smart > cursor + 5 ? smart : naturalCut;
          cursor = next;
          splits.push(cursor);
        }
      };

      for (const b of forced) {
        if (b <= cursor) continue;
        consumeUntil(b);
        if (b > cursor + 0.5) {
          cursor = b;
          splits.push(cursor);
        }
      }
      consumeUntil(totalMm);

      if (splits.length === 0) splits.push(0);

      setPageSplitsMm(splits);
      onMetrics?.({
        heightMm: totalMm + 2 * marginMm,
        pages: splits.length,
        pageHeightMm: dims.h,
        pageWidthMm: dims.w,
        marginMm,
        manualBreaks: manualBreaksMm.length,
        avoidBlocks: avoid.length,
      });
    };
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(measure); });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [data, style, dims.h, dims.w, marginMm, pageContentH, firstPageContentH, onMetrics]);

  // Physical page dimensions in px (unscaled).
  const pageWpx = dims.w * PX_PER_MM;
  const pageHpx = dims.h * PX_PER_MM;

  const contentWidthMm = dims.w - 2 * marginMm;
  const contentWidthPx = contentWidthMm * PX_PER_MM;

  const wrapperWpx = pageWpx * scale;
  // Total stack height = N pages scaled + (N-1) gaps
  const totalStackHpx = pages * pageHpx * scale + (pages - 1) * PAGE_GAP_PX;

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {/* Hidden measurement twin — renders the renderer once so we can measure
          content height and find [data-page-break] markers. Width matches the
          content width in mm so wrapping is identical to the visible slices. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: -99999,
          left: -99999,
          width: `${contentWidthPx}px`,
          pointerEvents: "none",
          visibility: "hidden",
        }}
      >
        <div ref={measureRef}>
          <ResumeRenderer data={data} style={style} />
        </div>
      </div>

      {/* Visible stack of page cards */}
      <div
        style={{
          position: "relative",
          width: `${wrapperWpx}px`,
          height: `${totalStackHpx}px`,
          margin: "0 auto",
          background: "transparent",
        }}
      >
        {pageSplitsMm.map((splitMm, i) => {
          const topOffsetPx = i * (pageHpx * scale + PAGE_GAP_PX);
          const isFirst = i === 0;
          const innerTopMm = isFirst && heroEdge ? 0 : marginMm;
          // This page shows content starting at splitMm. We shift the renderer up
          // by splitMm mm so that slice renders at the top of this page's content.
          const renderShiftMm = splitMm;
          // Sidebar bg strip — rendered on every page so the sidebar colour
          // extends full A4 height on every page, even when content is shorter.
          const sideBg = style?.sidebar_bg || "#F4F6FA";
          const leftRatio = Number(style?.left_ratio ?? 0.66);
          const singleCol = leftRatio >= 0.98;
          const sidePx = singleCol ? 0 : (dims.w - leftRatio * dims.w) * PX_PER_MM * scale;

          return (
            <div
              key={`page-${i}`}
              data-testid={`page-${i + 1}`}
              style={{
                position: "absolute",
                left: 0,
                top: `${topOffsetPx}px`,
                width: `${pageWpx * scale}px`,
                height: `${pageHpx * scale}px`,
                background: style?.page_bg || "#fff",
                boxShadow: "0 14px 40px -10px rgba(15, 23, 42, 0.28), 0 2px 10px rgba(15, 23, 42, 0.1)",
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid rgba(15, 23, 42, 0.08)",
              }}
            >
              {/* Sidebar bg strip — paints full page height so sidebar colour
                  is visually consistent across every page. Skipped on page 1
                  when hero is enabled (hero bleeds full-width there), but
                  painted on pages 2+ since the sidebar returns. */}
              {!singleCol && !(style?.hero_enabled && i === 0) && (
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: `${sidePx}px`,
                    background: sideBg,
                    zIndex: 1,
                  }}
                />
              )}
              {/* Top + bottom margin guide lines — shows the user the safe
                  print area on every page. */}
              <div aria-hidden="true" style={{ position: "absolute", top: `${marginMm * PX_PER_MM * scale}px`, left: 0, right: 0, borderTop: "1px dashed rgba(100, 116, 139, 0.25)", zIndex: 2, pointerEvents: "none" }} />
              <div aria-hidden="true" style={{ position: "absolute", bottom: `${marginMm * PX_PER_MM * scale}px`, left: 0, right: 0, borderTop: "1px dashed rgba(100, 116, 139, 0.25)", zIndex: 2, pointerEvents: "none" }} />
              {/* PAGE N / M badge — always visible */}
              <span
                data-testid={`page-badge-${i + 1}`}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 12,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.16em",
                  color: "rgba(15, 23, 42, 0.55)",
                  background: "rgba(255,255,255,0.92)",
                  padding: "3px 9px",
                  borderRadius: 999,
                  border: "1px solid rgba(15, 23, 42, 0.14)",
                  pointerEvents: "none",
                  zIndex: 5,
                  textTransform: "uppercase",
                }}
              >
                Page {i + 1} / {pages}
              </span>

              {/* Repeat header on pages 2+ (if template enables it) */}
              {style?.repeat_header && i > 0 && (
                <div
                  data-testid={`page-repeat-header-${i + 1}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    padding: `${6 * scale}px ${12 * scale}px`,
                    fontSize: `${8 * scale}pt`,
                    color: "rgba(15, 23, 42, 0.55)",
                    background: "rgba(250, 250, 252, 0.95)",
                    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                    zIndex: 4,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{(style.header_text || "").trim() || (data?.name || "")}</span>
                  <span>{i + 1} / {pages}</span>
                </div>
              )}

              {/* Repeat footer on every page (if template enables it) */}
              {style?.repeat_footer && (
                <div
                  data-testid={`page-repeat-footer-${i + 1}`}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: `${6 * scale}px ${12 * scale}px`,
                    fontSize: `${8 * scale}pt`,
                    color: "rgba(15, 23, 42, 0.55)",
                    background: "rgba(250, 250, 252, 0.95)",
                    borderTop: "1px solid rgba(15, 23, 42, 0.06)",
                    zIndex: 4,
                    textAlign: "center",
                  }}
                >
                  {(style.footer_text || "{name} · {title} · Page {page} of {pages}")
                    .replace("{name}", data?.name || "")
                    .replace("{title}", data?.title || "")
                    .replace("{page}", String(i + 1))
                    .replace("{pages}", String(pages))}
                </div>
              )}

              {/* Candidate signature image (bottom-left or bottom-right) — shown
                  only on the LAST page by default. */}
              {data?.signature_url && i === pages - 1 && (
                <div
                  data-testid={`page-signature-${i + 1}`}
                  style={{
                    position: "absolute",
                    bottom: `${(marginMm + 4) * PX_PER_MM * scale}px`,
                    [data?.signature_position === "left" ? "left" : "right"]: `${(marginMm + 4) * PX_PER_MM * scale}px`,
                    zIndex: 4,
                    textAlign: data?.signature_position === "left" ? "left" : "right",
                  }}
                >
                  <img src={data.signature_url} alt="signature" style={{ maxWidth: `${40 * PX_PER_MM * scale}px`, maxHeight: `${20 * PX_PER_MM * scale}px`, objectFit: "contain" }} />
                  {data.signature_label && (
                    <div style={{ fontSize: `${8 * scale}pt`, color: "rgba(15,23,42,0.6)", marginTop: `${2 * scale}px`, borderTop: "1px solid rgba(15,23,42,0.3)", paddingTop: `${2 * scale}px` }}>
                      {data.signature_label}
                    </div>
                  )}
                </div>
              )}

              {/* Clipping viewport — offset content by renderShiftMm so this page
                  shows the correct slice of the continuous renderer. */}
              <div
                style={{
                  position: "absolute",
                  top: `${innerTopMm * PX_PER_MM * scale}px`,
                  left: `${marginMm * PX_PER_MM * scale}px`,
                  width: `${contentWidthPx * scale}px`,
                  height: `${(dims.h - innerTopMm - marginMm) * PX_PER_MM * scale}px`,
                  overflow: "hidden",
                  zIndex: 3,
                }}
              >
                <div
                  style={{
                    width: `${contentWidthPx}px`,
                    transform: `scale(${scale}) translateY(${-renderShiftMm}mm)`,
                    transformOrigin: "top left",
                  }}
                >
                  <ResumeRenderer data={data} style={style} editable={editable} onTextEdit={onTextEdit} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
